import { api } from "@/lib/api/client";

export interface Participant {
  user_id: string;
  name: string;
  profile_picture_file_id: string | null;
}

export interface Conversation {
  id: string;
  kind: "direct" | "group";
  title: string;
  /** The other person on a direct thread, so the list can show their face. */
  counterpart: Participant | null;
  member_count: number;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_sender_name: string | null;
  unread: number;
  /** The group behind a group thread. Null on a direct one. */
  group_id: string | null;
  /** True when the group follows a position; false for one HR keeps by hand. */
  follows_position: boolean;
}

export interface GroupOption {
  group_id: string;
  name: string;
  description: string | null;
  follows_position: boolean;
  member_count: number;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string | null;
  sender_name: string | null;
  body: string;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
}

export const chatKeys = {
  all: ["chat"] as const,
  conversations: () => [...chatKeys.all, "conversations"] as const,
  unread: () => [...chatKeys.all, "unread"] as const,
  messages: (id: string) => [...chatKeys.all, "messages", id] as const,
  people: (q: string) => [...chatKeys.all, "people", q] as const,
  groups: () => [...chatKeys.all, "groups"] as const,
};

export const chatApi = {
  conversations: () => api.get<Conversation[]>("/me/conversations"),
  unread: () => api.get<{ unread: number }>("/me/conversations/unread-count"),
  people: (q: string) =>
    api.get<Participant[]>("/me/chat/people", { query: q ? { q } : undefined }),
  groups: () => api.get<GroupOption[]>("/me/chat/groups"),
  openDirect: (userId: string) =>
    api.post<Conversation>("/conversations/direct", { user_id: userId }),
  openGroup: (groupId: string) =>
    api.post<Conversation>("/conversations/group", { group_id: groupId }),
  /** HR makes a group by hand and opens its thread in one go. */
  createGroup: (name: string, userIds: string[]) =>
    api.post<Conversation>("/chat/groups", { name, user_ids: userIds }),
  messages: (id: string) => api.get<ChatMessage[]>(`/conversations/${id}/messages`),
  send: (id: string, body: string) =>
    api.post<ChatMessage>(`/conversations/${id}/messages`, { body }),
  markRead: (id: string) => api.post<{ message: string }>(`/conversations/${id}/read`, {}),
  withdraw: (messageId: string) => api.delete<ChatMessage>(`/messages/${messageId}`),
};
